import Service from './Service';
import {
  createIntl,
  createIntlCache,
  IntlShape,
  IntlCache,
} from '@formatjs/intl';
import type TokenizedApi from '../TokenizedApi';
import messagesEn from '../../../src/compiled-lang/en.json';
import { localeToCurrency } from '../utils/localeToCurrency';
import { CURRENCY_DEFAULT } from '../TokenizedApi';

export type TSdkIntlShape = IntlShape<Record<string, unknown>>;
export type TSdkIntlPart = string | Record<string, unknown>;

export const EVENT_INTLCHANGE_TYPE = 'intlchange';
export interface TIntlChangeEventDetail {
  intl: TSdkIntlShape;
  locale: string;
  currencyCode: string;
}
export type TIntlChangeEvent = CustomEvent<TIntlChangeEventDetail>;
export function isIntlChangeEvent(event: unknown): event is TIntlChangeEvent {
  return event instanceof Event && event.type === EVENT_INTLCHANGE_TYPE;
}

const matchWhitespace = /\s/g;
const typographicMinusSign = '−';

const numberParseInfoByLocale = new Map<
  string,
  ReturnType<typeof makeNumberParseInfo>
>();

function makeNumberParseInfo(intl: TSdkIntlShape) {
  const parts = intl.formatNumberToParts(12345.6);
  const numerals = [
    ...intl.formatNumber(9876543210, { useGrouping: false }),
  ].reverse();
  const index = new Map(numerals.map((d, i) => [d, i]));
  const group = new RegExp(
    `[${parts.find((d) => d.type === 'group')?.value || ''}]`,
    'g',
  );
  const decimal = new RegExp(
    `[${parts.find((d) => d.type === 'decimal')?.value || ''}]`,
  );
  const numeral = new RegExp(`[${numerals.join('')}]`, 'g');
  const indexGet = (d: string) => `${index.get(d) ?? ''}`;
  return {
    group,
    decimal,
    numeral,
    index: indexGet,
    parse: (inputNumber: string | number) => {
      if (typeof inputNumber === 'number') {
        return inputNumber;
      }
      if (typeof inputNumber === 'string') {
        const englishNumberString = inputNumber
          .replace(matchWhitespace, '')
          .replace(typographicMinusSign, '-')
          .replace(group, '')
          .replace(decimal, '.')
          .replace(numeral, indexGet);
        if (englishNumberString) {
          const englishNumber = +englishNumberString;
          if (!isNaN(englishNumber)) {
            return englishNumber;
          }
        }
      }
      // To allow mistakes to be corrected
      return inputNumber;
    },
  };
}

export function getNumberParseInfo(intl: TSdkIntlShape) {
  if (!intl) {
    return null;
  }
  let parseInfo = numberParseInfoByLocale.get(intl.locale);
  if (!parseInfo) {
    parseInfo = makeNumberParseInfo(intl);
    numberParseInfoByLocale.set(intl.locale, parseInfo);
  }
  return parseInfo;
}

/** @category SDK Services */
class LibraryIntl extends Service {
  /** @internal */
  cache: IntlCache;
  /** @internal */
  intl: TSdkIntlShape;

  /** @internal */
  constructor(tokenizedApi: TokenizedApi) {
    super(tokenizedApi);

    const config = this.tokenizedApi.config;

    let locale =
      window.navigator.languages?.[0] || window.navigator.language || 'en';
    if (config.locale) {
      if (typeof config.locale === 'string') {
        locale = config.locale;
      } else {
        throw new TypeError('Invalid config: locale must be a string or null');
      }
    }

    this.cache = createIntlCache();
    this.intl = createIntl(
      {
        locale,
        messages: messagesEn,
      },
      this.cache,
    );
  }

  getIntl() {
    return this.intl;
  }

  getLocale() {
    return this.intl.locale;
  }

  getLocaleCurrencyCode() {
    const locale = new Intl.Locale(this.intl.locale);
    const region = locale.maximize().region;
    if (region) {
      const localeCurrencyCode = localeToCurrency[region];
      if (localeCurrencyCode) {
        return localeCurrencyCode;
      }
    }
    return CURRENCY_DEFAULT;
  }

  notifyIntlChange() {
    const event = new CustomEvent<TIntlChangeEventDetail>(
      EVENT_INTLCHANGE_TYPE,
      {
        detail: {
          intl: this.getIntl(),
          locale: this.getLocale(),
          currencyCode: this.getLocaleCurrencyCode(),
        },
      },
    );
    this.dispatchEvent(event);
  }

  changeLocale(locale: string) {
    if (!locale || typeof locale !== 'string') {
      throw new TypeError('Locale must be a string');
    }
    this.cache = createIntlCache();
    this.intl = createIntl(
      {
        locale,
        messages: messagesEn,
      },
      this.cache,
    );
    this.notifyIntlChange();
    this.tokenizedApi.reloadAll();
  }

  parseNumber(inputNumber: string | number) {
    const numberParseInfo = getNumberParseInfo(this.intl);
    if (!numberParseInfo) {
      return null;
    }
    return numberParseInfo.parse(inputNumber);
  }
}

export default LibraryIntl;
